import React from 'react'
import { Input } from 'antd'
import type { DashboardDomain } from '../types'
import './org-dashboard.scss'

/** Whether a domain's name or hostname contains the term, ignoring case and the term's surrounding spaces. */
export const domainMatchesSearchTerm = (domain: DashboardDomain, searchTerm: string): boolean => {
    const normalizedTerm = searchTerm.trim().toLowerCase()
    return (
        normalizedTerm === '' ||
        domain.displayName.toLowerCase().includes(normalizedTerm) ||
        domain.hostname.toLowerCase().includes(normalizedTerm)
    )
}

type Props = {
    searchTerm: string
    onSearchTermChange: (searchTerm: string) => void
    matchedDomainCount?: number
    totalDomainCount?: number
    /**
     * Names the box for assistive technology. Sections that sit on one page each need their own, since the
     * placeholder is the same visible text in all of them.
     */
    accessibleName?: string
    /** The box stands in its place, unusable, while the rows it would search are still loading. */
    disabled?: boolean
}

/**
 * The search box in a table header, and the count of matches it announces to assistive technology once a term is
 * typed. The count is not drawn: text that appears beside the box would push the header's other controls along as
 * the term changes. It is a header filter, so it takes DataView's filter classes and, with them, DataView's search
 * width. It filters as the term is typed; the button only repeats what typing has already done.
 */
export const DomainSearch: React.FC<Props> = ({
    searchTerm,
    onSearchTermChange,
    matchedDomainCount = 0,
    totalDomainCount = 0,
    accessibleName = 'Search domains',
    disabled = false,
}) => (
    <>
        <Input.Search
            allowClear
            enterButton
            className="data-view-filter search"
            placeholder="Search domains"
            aria-label={accessibleName}
            disabled={disabled}
            value={searchTerm}
            onChange={(event) => onSearchTermChange(event.target.value)}
            onSearch={onSearchTermChange}
        />
        {!disabled && searchTerm.trim() ? (
            <span className="org-dashboard-visually-hidden" role="status">
                {`${matchedDomainCount} of ${totalDomainCount} domains match`}
            </span>
        ) : null}
    </>
)
